import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Download, ShieldAlert, ChevronRight, Loader2 } from "lucide-react";
import { AccountAPI } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

/**
 * DeleteAccountDialog — irreversible hard-delete with a two-step gate.
 *
 * Step 1: warning + "Download my data first" CTA (pre-highlighted, skippable)
 *         + an "I understand this is permanent" checkbox.
 * Step 2: type the literal word DELETE to enable the final destructive button.
 *
 * On success, clears the session locally and routes to the landing page.
 */
const CONFIRM_WORD = "DELETE";

export default function DeleteAccountDialog({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [understood, setUnderstood] = useState(false);
  const [confirmInput, setConfirmInput] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { setUser } = useAuth();

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setUnderstood(false);
    setConfirmInput("");
    setExporting(false);
    setExported(false);
    setDeleting(false);
  }, [open]);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Pre-deletion export always grabs the full history (archived
      // vehicles included) so users don't lose anything they might want.
      const { filename } = await AccountAPI.exportData({ includeArchived: true });
      setExported(true);
      toast.success(`Export ready: ${filename}`);
    } catch (e) {
      toast.error(e?.message || "Could not generate the export.");
    } finally {
      setExporting(false);
    }
  };

  const canConfirm = confirmInput.trim().toUpperCase() === CONFIRM_WORD;

  const handleDelete = async () => {
    if (!canConfirm) return;
    setDeleting(true);
    try {
      await AccountAPI.deleteAccount();
      // Clear local auth state — the cookie was cleared server-side too.
      try { setUser?.(null); } catch { /* noop */ }
      try { localStorage.removeItem("session_token"); } catch { /* noop */ }
      onOpenChange(false);
      toast.success("Your account has been deleted.");
      // Hard navigate to landing — replaces history so back-button can't reach the dashboard.
      window.location.replace("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Deletion failed.");
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-danger-soft)] text-[var(--text-primary)] sm:max-w-[480px]"
        data-testid="delete-account-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[#E82127]" strokeWidth={1.75} />
            <DialogTitle className="font-display text-2xl font-light tracking-tight">
              Delete your account
            </DialogTitle>
          </div>
          <DialogDescription className="text-[var(--text-secondary)] text-sm leading-relaxed pt-1">
            {step === 1
              ? "This permanently removes your account, all trips, energy logs, vehicles and settings. There is no recovery."
              : "Type DELETE in capital letters to confirm. This cannot be undone."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            {/* Pre-highlighted export CTA */}
            <div
              className="border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4"
              style={{ borderRadius: 4 }}
              data-testid="delete-export-cta"
            >
              <div className="flex items-start gap-3">
                <Download className="w-4 h-4 text-[var(--text-primary)] mt-0.5" strokeWidth={1.75} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--text-primary)] tracking-tight">Download your data first</div>
                  <div className="text-[12px] text-[var(--text-secondary)] mt-0.5 leading-relaxed">
                    Get a ZIP with your trips, energy logs and vehicle config. Skip this step only if you&apos;ve already exported.
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      onClick={handleExport}
                      disabled={exporting}
                      className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)] h-8 text-xs"
                      data-testid="delete-step1-export-btn"
                    >
                      {exporting ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1.5 animate-spin" strokeWidth={2} />
                          Generating...
                        </>
                      ) : exported ? (
                        "Download again"
                      ) : (
                        "Download my data"
                      )}
                    </Button>
                    {exported && (
                      <span className="text-[11px] text-emerald-300 tracking-wider" data-testid="delete-export-done">
                        ✓ Saved
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* What will be deleted */}
            <div className="text-xs text-[var(--text-secondary)] space-y-1.5 leading-relaxed">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)] mb-1.5">What gets wiped</div>
              <div>· Account profile + email</div>
              <div>· All vehicles + insurance details</div>
              <div>· All trips and energy events</div>
              <div>· Currency / timezone / deductible preferences</div>
              <div>· Active sessions across all devices</div>
            </div>

            <label
              className="flex items-start gap-2.5 cursor-pointer select-none"
              data-testid="delete-understand-label"
            >
              <Checkbox
                checked={understood}
                onCheckedChange={(v) => setUnderstood(!!v)}
                className="mt-0.5 border-[var(--border-strong)] data-[state=checked]:bg-[#E82127] data-[state=checked]:border-[#E82127]"
                data-testid="delete-understand-checkbox"
              />
              <span className="text-xs text-[var(--text-primary)]/85 leading-relaxed">
                I understand this is permanent and cannot be undone.
              </span>
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div
              className="flex items-start gap-2 px-3 py-2.5 border border-[var(--border-danger-soft)] bg-[var(--bg-danger-soft)] text-[#fca5a5] text-xs leading-relaxed"
              style={{ borderRadius: 4 }}
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
              <span>
                Last warning — clicking the button below permanently erases everything tied to your account.
              </span>
            </div>
            <div className="space-y-2">
              <Label className="label-tiny">
                Type <span className="font-mono-data text-[var(--text-primary)]">DELETE</span> to confirm
              </Label>
              <Input
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="DELETE"
                autoFocus
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data tracking-[0.18em]"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
            className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            data-testid="delete-cancel-btn"
          >
            Cancel
          </Button>
          {step === 1 ? (
            <Button
              onClick={() => setStep(2)}
              disabled={!understood}
              className="bg-[#E82127] text-white hover:bg-[#c01b20] disabled:bg-[#3a1212] disabled:text-[var(--text-muted)]"
              data-testid="delete-step1-continue-btn"
            >
              Continue
              <ChevronRight className="w-3.5 h-3.5 ml-1" strokeWidth={2} />
            </Button>
          ) : (
            <Button
              onClick={handleDelete}
              disabled={!canConfirm || deleting}
              className="bg-[#E82127] text-white hover:bg-[#c01b20] disabled:bg-[#3a1212] disabled:text-[var(--text-muted)]"
              data-testid="delete-confirm-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} />
                  Deleting...
                </>
              ) : (
                "Delete my account"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
